import { useCallback } from 'react';
import * as intrfce from '../Interfaces';
import { updateSavedAddressWithId0 } from '../utils/addressUtils';

interface UseAddressHandlersProps {
  setFormBillingAddress: React.Dispatch<React.SetStateAction<intrfce.iAddressFormData>>;
  setFormBillingData: React.Dispatch<React.SetStateAction<intrfce.iBillingFormData>>;
  setFormData: React.Dispatch<React.SetStateAction<intrfce.iFormContainer>>;
  setFormShippingAddress: React.Dispatch<React.SetStateAction<intrfce.iAddressFormData>>;
  setSavedBillingAddress: React.Dispatch<React.SetStateAction<intrfce.iSavedAddress[]>>;
  setSavedShippingAddress: React.Dispatch<React.SetStateAction<intrfce.iSavedAddress[]>>;
  setSelectedBillingAddress: (value: intrfce.iSavedAddress | "reset" | ((prev: intrfce.iSavedAddress | "reset" | null) => intrfce.iSavedAddress | "reset" | null) | null) => void;
  setSelectedShippingAddress: (value: any) => void;
  validateForm: (fieldName: string, value: string | {} | boolean, formType: "" | "billing" | "shipping") => boolean;
}

export const useAddressHandlers = (props: UseAddressHandlersProps) => {
  const {
    setFormBillingAddress,
    setFormBillingData,
    setFormData,
    setFormShippingAddress,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setSelectedBillingAddress,
    setSelectedShippingAddress,
    validateForm
  } = props;

  /**
   * Updates the billing address with suggested/recommended address
   */
  const updateSuggestedBillingAddress = useCallback((suggestedAddr: intrfce.iSavedAddress) => {
    console.log("updateSuggestedBillingAddress==>", suggestedAddr);

    // Validate each field
    Object.keys(suggestedAddr).forEach((fieldName) => {
      const value = suggestedAddr[fieldName as keyof intrfce.iSavedAddress];
      if (value && typeof value === 'string' && value.length) {
        validateForm(fieldName, value, "billing");
      }
    });

    // Update savedBillingAddress - replace object with id 0 if it exists, otherwise add it
    setSavedBillingAddress((prev) => updateSavedAddressWithId0(prev, suggestedAddr));

    // Update form data with the same address format
    setFormBillingAddress({
      pincode: suggestedAddr.postalCode || "",
      address: suggestedAddr.line1 || "",
      line2: suggestedAddr.line2 || "",
      cityValue: suggestedAddr.city || "",
      stateValue: suggestedAddr.countrySubdivision || "",
      countryName: suggestedAddr.countryName || "",
      countryValue: suggestedAddr.country || "",
    });

    // Update formBillingData with the recommended address
    setFormBillingData((prevData) => ({
      ...prevData,
      firstNameBilling: suggestedAddr.firstName || prevData.firstNameBilling,
      lastNameBilling: suggestedAddr.lastName || prevData.lastNameBilling,
      contactBilling: suggestedAddr.phoneNumber || prevData.contactBilling,
    }));

    // Update selectedBillingAddress with id: 0
    setSelectedBillingAddress(suggestedAddr);
  }, [validateForm, setSavedBillingAddress, setFormBillingAddress, setFormBillingData, setSelectedBillingAddress]);

  /**
   * Updates the shipping address with suggested/recommended address
   */
  const updateSuggestedShippingAddress = useCallback((suggestedAddr: intrfce.iSavedAddress) => {
    console.log("updateSuggestedShippingAddress==>", suggestedAddr);

    // Validate each field
    Object.keys(suggestedAddr).forEach((fieldName) => {
      const value = suggestedAddr[fieldName as keyof intrfce.iSavedAddress];
      if (value && typeof value === 'string' && value.length) {
        validateForm(fieldName, value, "shipping");
      }
    });

    // Update savedShippingAddress - replace object with id 0 if it exists, otherwise add it
    setSavedShippingAddress((prev) => updateSavedAddressWithId0(prev, suggestedAddr));

    // Update form data with the same address format
    setFormShippingAddress({
      pincode: suggestedAddr.postalCode || "",
      address: suggestedAddr.line1 || "",
      line2: suggestedAddr.line2 || "",
      cityValue: suggestedAddr.city || "",
      stateValue: suggestedAddr.countrySubdivision || "",
      countryName: suggestedAddr.countryName || "",
      countryValue: suggestedAddr.country || "",
    });

    // Update formData with id: 0 and the recommended address
    setFormData((prevData) => ({
      ...prevData,
      id: 0,
      firstNameShiping: suggestedAddr.firstName || prevData.firstNameShiping,
      lastNameShiping: suggestedAddr.lastName || prevData.lastNameShiping,
      contactShiping: suggestedAddr.phoneNumber || prevData.contactShiping,
      iShippingFormAddress: {
        pincode: suggestedAddr.postalCode || "",
        address: suggestedAddr.line1 || "",
        line2: suggestedAddr.line2 || "",
        cityValue: suggestedAddr.city || "",
        stateValue: suggestedAddr.countrySubdivision || "",
        countryName: suggestedAddr.countryName || "",
        countryValue: suggestedAddr.country || "",
      },
    }));

    // Update selectedShippingAddress with id: 0
    setSelectedShippingAddress(suggestedAddr);
  }, [validateForm, setSavedShippingAddress, setFormShippingAddress, setFormData, setSelectedShippingAddress]);

  return {
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress
  };
};

